/**
 * Error Logger Service
 * 
 * Provides centralized error logging with context capture for debugging
 * and monitoring. Implements structured logging with categorization,
 * stack traces, and detailed context information.
 * 
 * Key Features:
 * - Centralized error logging with consistent format
 * - Context capture (component, route, user action)
 * - Error categorization for filtering and analysis
 * - Stack trace capture for debugging
 * - Structured logging for easy parsing
 * - Integration-ready for external monitoring services
 * 
 * @module errorLogger
 */

/**
 * Context information for error logging
 */
export interface ErrorContext {
  /** Component or module where error occurred */
  component?: string;
  /** User action that triggered the error */
  action?: string;
  /** Current route or page */
  route?: string;
  /** Additional contextual data */
  additionalData?: Record<string, any>;
}

/**
 * Error category for classification
 */
export const ErrorCategory = {
  /** API-related errors (network, response format, etc.) */
  API: 'API',
  /** Type errors (wrong type, null reference, etc.) */
  TYPE: 'TYPE',
  /** Validation errors (invalid data, schema mismatch, etc.) */
  VALIDATION: 'VALIDATION',
  /** Network errors (timeout, connection failed, etc.) */
  NETWORK: 'NETWORK',
  /** Authentication/Authorization errors */
  AUTH: 'AUTH',
  /** UI/Component errors */
  UI: 'UI',
  /** Cache-related errors (encoding, compression, storage, etc.) */
  CACHE: 'CACHE',
  /** Timeout-related errors */
  TIMEOUT: 'TIMEOUT',
  /** General application errors */
  GENERAL: 'GENERAL'
} as const;

export type ErrorCategory = typeof ErrorCategory[keyof typeof ErrorCategory];

/**
 * Severity level for errors
 */
export const ErrorSeverity = {
  /** Low severity - informational */
  INFO: 'INFO',
  /** Medium severity - warning */
  WARNING: 'WARNING',
  /** High severity - error */
  ERROR: 'ERROR',
  /** Critical severity - requires immediate attention */
  CRITICAL: 'CRITICAL'
} as const;

export type ErrorSeverity = typeof ErrorSeverity[keyof typeof ErrorSeverity];

/**
 * Structured error log entry
 */
interface ErrorLogEntry {
  /** Timestamp of error */
  timestamp: string;
  /** Error category */
  category: ErrorCategory;
  /** Error severity */
  severity: ErrorSeverity;
  /** Error message */
  message: string;
  /** Error stack trace (if available) */
  stack?: string;
  /** Context information */
  context?: ErrorContext;
  /** Additional error details */
  details?: Record<string, any>;
}

/**
 * Error Logger Service
 * 
 * Centralized service for logging errors with context and categorization.
 * Provides structured logging that can be easily integrated with external
 * monitoring services like Sentry, LogRocket, or custom logging backends.
 */
export class ErrorLogger {
  /**
   * Log API response validation errors
   * 
   * Logs detailed information about API response validation failures,
   * including the endpoint, expected structure, and actual response.
   * 
   * @param endpoint - API endpoint that was called
   * @param expectedStructure - Description of expected response structure
   * @param actualResponse - Actual response received from API
   * @param context - Additional context information
   * 
   * @example
   * ```typescript
   * ErrorLogger.logAPIValidationError(
   *   '/api/station-metadata',
   *   'Response should contain data.stations array',
   *   response,
   *   { component: 'WeatherStationAPI', action: 'getAllStationsForMap' }
   * );
   * ```
   */
  static logAPIValidationError(
    endpoint: string,
    expectedStructure: string,
    actualResponse: any,
    context?: ErrorContext
  ): void {
    const message = `API validation failed for ${endpoint}`;
    
    const details = {
      endpoint,
      expectedStructure,
      actualResponseType: typeof actualResponse,
      actualResponseKeys: actualResponse && typeof actualResponse === 'object' 
        ? Object.keys(actualResponse) 
        : [],
      // Include sanitized response structure (avoid logging sensitive data)
      responseStructure: this.sanitizeResponseForLogging(actualResponse)
    };
    
    const logEntry: ErrorLogEntry = {
      timestamp: new Date().toISOString(),
      category: ErrorCategory.API,
      severity: ErrorSeverity.ERROR,
      message,
      context,
      details
    };
    
    this.logEntry(logEntry);
    
    // Also log to console for development
    console.error(`[ErrorLogger] ${message}`);
    console.error('[ErrorLogger] Expected:', expectedStructure);
    console.error('[ErrorLogger] Actual response structure:', details.responseStructure);
    if (context) {
      console.error('[ErrorLogger] Context:', context);
    }
  }
  
  /**
   * Log type mismatch errors
   * 
   * Logs errors related to type mismatches, such as calling string methods
   * on non-string values. Includes the operation attempted, expected type,
   * actual type, and the problematic value.
   * 
   * @param operation - Operation that was attempted (e.g., '.match()')
   * @param expectedType - Expected data type
   * @param actualType - Actual data type received
   * @param value - The problematic value (sanitized for logging)
   * @param context - Additional context information
   * 
   * @example
   * ```typescript
   * ErrorLogger.logTypeError(
   *   '.match()',
   *   'string',
   *   typeof value,
   *   value,
   *   { component: 'StationTransformer', action: 'extractStationCode' }
   * );
   * ```
   */
  static logTypeError(
    operation: string,
    expectedType: string,
    actualType: string,
    value: any,
    context?: ErrorContext
  ): void {
    const message = `Type error: Cannot perform ${operation} on ${actualType} (expected ${expectedType})`;
    
    const details = {
      operation,
      expectedType,
      actualType,
      // Sanitize value for logging (truncate if too long, hide sensitive data)
      value: this.sanitizeValueForLogging(value),
      valueConstructor: value?.constructor?.name || 'unknown'
    };
    
    const logEntry: ErrorLogEntry = {
      timestamp: new Date().toISOString(),
      category: ErrorCategory.TYPE,
      severity: ErrorSeverity.ERROR,
      message,
      context,
      details
    };
    
    this.logEntry(logEntry);
    
    // Also log to console for development
    console.error(`[ErrorLogger] ${message}`);
    console.error('[ErrorLogger] Details:', details);
    if (context) {
      console.error('[ErrorLogger] Context:', context);
    }
  }
  
  /**
   * Log general errors
   * 
   * Logs general application errors with full stack trace and context.
   * This is the catch-all method for errors that don't fit other categories.
   * 
   * @param error - Error object or error message
   * @param context - Additional context information
   * @param category - Error category (default: GENERAL)
   * @param severity - Error severity (default: ERROR)
   * 
   * @example
   * ```typescript
   * try {
   *   // Some operation
   * } catch (error) {
   *   ErrorLogger.logError(
   *     error,
   *     { component: 'MapComponent', action: 'renderStations' }
   *   );
   * }
   * ```
   */
  static logError(
    error: Error | string,
    context?: ErrorContext,
    category: ErrorCategory = ErrorCategory.GENERAL,
    severity: ErrorSeverity = ErrorSeverity.ERROR
  ): void {
    const isErrorObject = error instanceof Error;
    const message = isErrorObject ? error.message : String(error);
    const stack = isErrorObject ? error.stack : undefined;
    
    const details: Record<string, any> = {
      errorType: isErrorObject ? error.constructor.name : 'string',
    };
    
    // Add error properties if available
    if (isErrorObject) {
      // Include any custom properties on the error object
      Object.keys(error).forEach(key => {
        if (key !== 'message' && key !== 'stack' && key !== 'name') {
          details[key] = this.sanitizeValueForLogging((error as any)[key]);
        }
      });
    }
    
    const logEntry: ErrorLogEntry = {
      timestamp: new Date().toISOString(),
      category,
      severity,
      message,
      stack,
      context,
      details
    };
    
    this.logEntry(logEntry);
    
    // Also log to console for development
    console.error(`[ErrorLogger] ${message}`);
    if (stack) {
      console.error('[ErrorLogger] Stack trace:', stack);
    }
    if (context) {
      console.error('[ErrorLogger] Context:', context);
    }
    if (Object.keys(details).length > 1) {
      console.error('[ErrorLogger] Details:', details);
    }
  }
  
  /**
   * Log validation errors
   * 
   * Logs errors related to data validation failures.
   * 
   * @param validationTarget - What was being validated
   * @param validationRules - Rules that failed
   * @param actualValue - Actual value that failed validation
   * @param context - Additional context information
   */
  static logValidationError(
    validationTarget: string,
    validationRules: string,
    actualValue: any,
    context?: ErrorContext
  ): void {
    const message = `Validation failed for ${validationTarget}`;
    
    const details = {
      validationTarget,
      validationRules,
      actualValue: this.sanitizeValueForLogging(actualValue),
      actualType: typeof actualValue
    };
    
    const logEntry: ErrorLogEntry = {
      timestamp: new Date().toISOString(),
      category: ErrorCategory.VALIDATION,
      severity: ErrorSeverity.WARNING,
      message,
      context,
      details
    };
    
    this.logEntry(logEntry);
    
    console.warn(`[ErrorLogger] ${message}`);
    console.warn('[ErrorLogger] Rules:', validationRules);
    console.warn('[ErrorLogger] Actual value:', details.actualValue);
  }
  
  /**
   * Log network errors
   * 
   * Logs errors related to network requests (timeouts, connection failures, etc.)
   * 
   * @param endpoint - API endpoint that failed
   * @param errorMessage - Error message
   * @param statusCode - HTTP status code (if available)
   * @param context - Additional context information
   */
  static logNetworkError(
    endpoint: string,
    errorMessage: string,
    statusCode?: number,
    context?: ErrorContext
  ): void {
    const message = `Network error for ${endpoint}: ${errorMessage}`;
    
    const details = {
      endpoint,
      errorMessage,
      statusCode,
      isTimeout: errorMessage.toLowerCase().includes('timeout'),
      isConnectionError: errorMessage.toLowerCase().includes('connection')
    };
    
    const severity = statusCode && statusCode >= 500 
      ? ErrorSeverity.CRITICAL 
      : ErrorSeverity.ERROR;
    
    const logEntry: ErrorLogEntry = {
      timestamp: new Date().toISOString(),
      category: ErrorCategory.NETWORK,
      severity,
      message,
      context,
      details
    };
    
    this.logEntry(logEntry);
    
    console.error(`[ErrorLogger] ${message}`);
    console.error('[ErrorLogger] Details:', details);
  }
  
  /**
   * Log cache errors
   * 
   * Logs errors related to cache operations (encoding, compression, storage, etc.)
   * 
   * @param operation - Cache operation that failed
   * @param errorMessage - Error message
   * @param context - Additional context information
   */
  static logCacheError(
    operation: string,
    errorMessage: string,
    context?: ErrorContext
  ): void {
    const message = `Cache error during ${operation}: ${errorMessage}`;
    
    const details = {
      operation,
      errorMessage,
      isEncodingError: errorMessage.toLowerCase().includes('encod'),
      isCompressionError: errorMessage.toLowerCase().includes('compress'),
      isStorageError: errorMessage.toLowerCase().includes('storage') || errorMessage.toLowerCase().includes('quota')
    };
    
    const logEntry: ErrorLogEntry = {
      timestamp: new Date().toISOString(),
      category: ErrorCategory.CACHE,
      severity: ErrorSeverity.WARNING,
      message,
      context,
      details
    };
    
    this.logEntry(logEntry);
    
    console.error(`[ErrorLogger] ${message}`);
    console.error('[ErrorLogger] Details:', details);
  }
  
  /**
   * Log timeout errors
   * 
   * Logs errors related to operation timeouts
   * 
   * @param operation - Operation that timed out
   * @param duration - How long the operation took
   * @param threshold - Timeout threshold
   * @param context - Additional context information
   */
  static logTimeoutError(
    operation: string,
    duration: number,
    threshold: number,
    context?: ErrorContext
  ): void {
    const message = `Timeout during ${operation}: ${duration}ms (threshold: ${threshold}ms)`;
    
    const details = {
      operation,
      duration,
      threshold,
      exceededBy: duration - threshold
    };
    
    const logEntry: ErrorLogEntry = {
      timestamp: new Date().toISOString(),
      category: ErrorCategory.TIMEOUT,
      severity: ErrorSeverity.ERROR,
      message,
      context,
      details
    };
    
    this.logEntry(logEntry);
    
    console.error(`[ErrorLogger] ${message}`);
    console.error('[ErrorLogger] Details:', details);
  }
  
  /**
   * Internal method to process and store log entry
   * 
   * This method can be extended to send logs to external services
   * like Sentry, LogRocket, or custom logging backends.
   * 
   * @param logEntry - Structured log entry
   */
  private static logEntry(logEntry: ErrorLogEntry): void {
    // Store in memory for debugging (limited to last 100 entries)
    this.errorHistory.push(logEntry);
    if (this.errorHistory.length > 100) {
      this.errorHistory.shift();
    }
    
    // Send to Sentry if available
    if (this.sentryMonitoring) {
      try {
        const error = new Error(logEntry.message);
        if (logEntry.stack) {
          error.stack = logEntry.stack;
        }
        
        this.sentryMonitoring.captureError(
          error,
          logEntry.context,
          logEntry.category,
          logEntry.severity
        );
      } catch (e) {
        console.error('[ErrorLogger] Failed to send to Sentry:', e);
      }
    }
    
    // In development, also log structured entry
    if (process.env.NODE_ENV === 'development') {
      console.debug('[ErrorLogger] Structured log entry:', logEntry);
    }
  }
  
  /**
   * Set Sentry monitoring instance
   * 
   * Allows integration with Sentry for external error tracking.
   * 
   * @param monitoring - Sentry monitoring instance
   */
  static setSentryMonitoring(monitoring: any): void {
    this.sentryMonitoring = monitoring;
  }
  
  /**
   * Sentry monitoring instance (optional)
   */
  private static sentryMonitoring: any = null;
  
  /**
   * Sanitize response for logging
   * 
   * Removes sensitive data and truncates large responses to prevent
   * logging excessive data.
   * 
   * @param response - Response to sanitize
   * @returns Sanitized response structure
   */
  private static sanitizeResponseForLogging(response: any): any {
    if (!response) return response;
    
    // If response is too large, just return structure info
    const responseStr = JSON.stringify(response);
    if (responseStr.length > 10000) {
      return {
        _truncated: true,
        _size: responseStr.length,
        _type: typeof response,
        _keys: typeof response === 'object' ? Object.keys(response) : []
      };
    }
    
    // Return sanitized copy
    try {
      const sanitized = JSON.parse(responseStr);
      
      // Remove common sensitive fields
      const sensitiveFields = ['password', 'token', 'apiKey', 'secret', 'authorization'];
      this.removeSensitiveFields(sanitized, sensitiveFields);
      
      return sanitized;
    } catch (e) {
      return {
        _error: 'Failed to sanitize response',
        _type: typeof response
      };
    }
  }
  
  /**
   * Sanitize value for logging
   * 
   * Truncates long strings and removes sensitive data.
   * 
   * @param value - Value to sanitize
   * @returns Sanitized value
   */
  private static sanitizeValueForLogging(value: any): any {
    if (value === null || value === undefined) {
      return value;
    }
    
    // Truncate long strings
    if (typeof value === 'string' && value.length > 500) {
      return value.substring(0, 500) + '... (truncated)';
    }
    
    // Truncate large objects/arrays
    if (typeof value === 'object') {
      try {
        const str = JSON.stringify(value);
        if (str.length > 1000) {
          return {
            _truncated: true,
            _type: Array.isArray(value) ? 'array' : 'object',
            _size: str.length,
            _preview: str.substring(0, 200) + '...'
          };
        }
        return value;
      } catch (e) {
        return {
          _error: 'Cannot stringify value',
          _type: typeof value
        };
      }
    }
    
    return value;
  }
  
  /**
   * Remove sensitive fields from object recursively
   * 
   * @param obj - Object to sanitize
   * @param sensitiveFields - List of field names to remove
   */
  private static removeSensitiveFields(obj: any, sensitiveFields: string[]): void {
    if (!obj || typeof obj !== 'object') return;
    
    for (const key in obj) {
      if (sensitiveFields.some(field => key.toLowerCase().includes(field.toLowerCase()))) {
        obj[key] = '[REDACTED]';
      } else if (typeof obj[key] === 'object') {
        this.removeSensitiveFields(obj[key], sensitiveFields);
      }
    }
  }
  
  /**
   * Get error history
   * 
   * Returns the last N error log entries for debugging.
   * 
   * @param limit - Maximum number of entries to return (default: 10)
   * @returns Array of recent error log entries
   */
  static getErrorHistory(limit: number = 10): ErrorLogEntry[] {
    return this.errorHistory.slice(-limit);
  }
  
  /**
   * Clear error history
   * 
   * Clears the in-memory error history.
   */
  static clearErrorHistory(): void {
    this.errorHistory = [];
  }
  
  /**
   * Get error statistics
   * 
   * Returns statistics about logged errors.
   * 
   * @returns Error statistics by category and severity
   */
  static getErrorStatistics(): {
    total: number;
    byCategory: Record<ErrorCategory, number>;
    bySeverity: Record<ErrorSeverity, number>;
  } {
    const stats = {
      total: this.errorHistory.length,
      byCategory: {} as Record<ErrorCategory, number>,
      bySeverity: {} as Record<ErrorSeverity, number>
    };
    
    // Initialize counters
    Object.values(ErrorCategory).forEach(category => {
      stats.byCategory[category] = 0;
    });
    Object.values(ErrorSeverity).forEach(severity => {
      stats.bySeverity[severity] = 0;
    });
    
    // Count errors
    this.errorHistory.forEach(entry => {
      stats.byCategory[entry.category]++;
      stats.bySeverity[entry.severity]++;
    });
    
    return stats;
  }
  
  /**
   * In-memory error history (last 100 entries)
   */
  private static errorHistory: ErrorLogEntry[] = [];
}
